import { Link, useNavigate } from 'react-router-dom'
import { X, LogOut, LayoutDashboard } from 'lucide-react'
import { navLinks, dropdownLinks } from '@/data/mock-data'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/auth-context'

interface MobileNavProps {
    isOpen: boolean
    onClose: () => void
}

export default function MobileNav({ isOpen, onClose }: MobileNavProps) {
    const { isAuthenticated, user, logout } = useAuth()
    const navigate = useNavigate()

    const handleNavigation = (path: string) => {
        navigate(path)
        onClose()
    }

    const handleLogout = () => {
        logout()
        navigate('/')
        onClose()
    }

    return (
        <>
            {/* Overlay */}
            <div
                className={cn(
                    'fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden',
                    isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                )}
                onClick={onClose}
            />

            {/* Mobile Menu */}
            <aside
                className={cn(
                    'fixed top-0 right-0 h-full w-80 bg-slate-900 border-l border-slate-700/50 z-50 transition-transform duration-300 lg:hidden',
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
                        <span className="text-xl font-display font-bold text-white">Menu</span>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-white transition-colors"
                            aria-label="Close menu"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Navigation Links */}
                    <nav className="flex-1 overflow-y-auto py-8">
                        <div className="space-y-2 px-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={onClose}
                                    className="block px-4 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-200"
                                >
                                    {link.label}
                                </Link>
                            ))}

                            {/* Explore Group */}
                            <div className="pt-3 mt-3 border-t border-slate-700/50">
                                <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Explore</p>
                                {dropdownLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        onClick={onClose}
                                        className="block px-4 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-200"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </nav>

                    {/* CTA Buttons */}
                    <div className="p-6 border-t border-slate-700/50 space-y-3">
                        {isAuthenticated ? (
                            <>
                                <button
                                    onClick={() => handleNavigation(user?.role === 'mentor' ? '/mentor/dashboard' : '/dashboard')}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-primary-blue hover:bg-blue-600 rounded-lg glow-subtle hover:glow-primary transition-all duration-200"
                                >
                                    <LayoutDashboard className="h-4 w-4" />
                                    Dashboard
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-slate-300 border border-slate-600 hover:border-red-500 hover:text-red-500 rounded-lg transition-colors duration-200"
                                >
                                    <LogOut className="h-4 w-4" />
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => handleNavigation('/login')}
                                    className="w-full px-4 py-3 text-sm font-medium text-white border border-slate-600 hover:border-primary-blue rounded-lg transition-colors duration-200"
                                >
                                    Login
                                </button>
                                <button
                                    onClick={() => handleNavigation('/signup')}
                                    className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-blue hover:bg-blue-600 rounded-lg glow-subtle hover:glow-primary transition-all duration-200"
                                >
                                    Sign Up
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </aside>
        </>
    )
}
